/** Restituisce i nomi delle proprietà del tipo @see T richiesto */
export const keysOf =
  <T>() =>
  (name: keyof T) =>
    name

export function isURL(str: string): boolean {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ) // fragment locator
  return !!pattern.test(str)
}

export function hasSpecialCharacters(str: string): boolean {
  const specialChars = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/g
  return specialChars.test(str)
}

export function reduceNotNull<T>(array: (T | null | undefined)[] | null | undefined): T[] {
  return (
    array?.reduce((previousValue: T[], currentValue: T | null | undefined) => {
      if (currentValue !== null && currentValue !== undefined) return previousValue.concat(currentValue)
      return previousValue
    }, []) || []
  )
}

export function range(start: number, end: number) {
  return Array.from(Array(end - start + 1).keys()).map((i) => i + start)
}

/**
 * Given an object as input return the stringified object as escaped json.
 */
export function escapeJsonObject(object: object) {
  return JSON.stringify(object).replace(/"/g, '\\"');
}

export function getRandomInt(max: number) {
  return Math.floor(Math.random() * max)
}

export function dateEquality(firstDate: Date, secondDate: Date) {
  return firstDate.getFullYear() === secondDate.getFullYear() && firstDate.getMonth() === secondDate.getMonth() && firstDate.getDate() === secondDate.getDate()
}

export function dateGreaterThan(firstDate: Date, secondDate: Date) {
  const yearGreaterThan = firstDate.getFullYear() > secondDate.getFullYear()
  const yearEqual = firstDate.getFullYear() === secondDate.getFullYear()
  const monthGreaterThan = firstDate.getMonth() > secondDate.getMonth()
  const monthEqual = firstDate.getMonth() === secondDate.getMonth()
  const dayGreaterThan = firstDate.getDate() > secondDate.getDate()
  return yearGreaterThan || (yearEqual && monthGreaterThan) || (monthEqual && dayGreaterThan)
}

export function dateLowerThan(firstDate: Date, secondDate: Date) {
  const yearLowerThan = firstDate.getFullYear() < secondDate.getFullYear()
  const yearEqual = firstDate.getFullYear() === secondDate.getFullYear()
  const monthLowerThan = firstDate.getMonth() < secondDate.getMonth()
  const monthEqual = firstDate.getMonth() === secondDate.getMonth()
  const dayLowerThan = firstDate.getDate() < secondDate.getDate()
  return yearLowerThan || (yearEqual && monthLowerThan) || (monthEqual && dayLowerThan)
}

export function popupCenter(url: string, title: string, w: number, h: number) {
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY

  const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width
  const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height

  const systemZoom = width / window.screen.availWidth
  const left = (width - w) / 2 / systemZoom + dualScreenLeft
  const top = (height - h) / 2 / systemZoom + dualScreenTop

  return window.open(url, title, `scrollbars=yes,width=${w / systemZoom},height=${h / systemZoom},left=${left},top=${top}`)
}

export function textInitialsUppercase(text?: string): string {
  return (
    text
      ?.split(' ')
      ?.map((i) => i.charAt(0).toUpperCase())
      .reduce((acc, curr) => acc + curr) ?? ''
  )
}

export function stringifyErrorReplacer(key: string, value: any): any {
  if (value instanceof Error) {
    const error: Record<string, any> = {}
    Object.getOwnPropertyNames(value).forEach((n: string) => {
      error[n] = (value as any)[n]
    })
    return error
  }
  return value
}

export function hasDuplicates(array: string[]) {
  const valuesSoFar = Object.create(null)
  for (let i = 0; i < array.length; ++i) {
    const value = array[i]
    if (value in valuesSoFar) {
      return true
    }
    valuesSoFar[value] = true
  }
  return false
}

export function hasDuplicateString(array: string[]) {
  for (const str in array) {
    const numberOfInstances = array.filter((s) => s === str)
    if (numberOfInstances.length > 1) {
      return true
    }
  }
  return false
}

/** Check if all items of second array are included in first one */
export const isIncludedArray = (firstArray: string[], secondArray: string[]) => {
  return firstArray.every((item) => secondArray.includes(item))
}

export function groupBy<T extends Record<K, any>, K extends keyof T>(array: T[], key: K): Record<T[K], T[]> {
  return array.reduce((obj, item) => {
    const keyValue = item[key]
    if (!obj[keyValue]) {
      obj[keyValue] = []
    }
    obj[keyValue].push(item)
    return obj
  }, {} as Record<T[K], T[]>)
}

export function firstLetterUppercase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}